
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Post-launch operations',
  layout: 'guide',
  description: 'Monitor integration health, track key metrics, and get support after going live',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Post-launch operations' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "overview"
      }}>{`Overview`}</h2>
      <p>{`After launch, the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/post-launch/dashboard"
        }}>{`Data Partner Dashboard`}</a>{` becomes the primary hub for monitoring integration health.`}</p>
      <p>{`You will have visibility into API errors, OAuth conversion trends, latency, response behavior, and data extraction success rates.`}</p>
      <p>{`The first few weeks post-launch are critical. Real-world usage may surface issues not seen during testing, so teams should be prepared to respond quickly.`}</p>
      <h2 {...{
        "id": "target-metrics"
      }}>{`Target metrics`}</h2>
      <p>{`Your OAuth server should meet these performance targets to ensure a smooth user experience and reliable API access. Plaid also monitors integration health to help identify and resolve performance issues.`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Metric`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Target`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Notes`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Endpoint latency`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`< 3.5 seconds at P95`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Response time for authorization, token, userinfo, and JWKS endpoints.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Throughput (TPS)`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`300+ requests/second`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Sustained throughput across all OAuth endpoints combined.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Uptime`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`99.5% minimum (99.9% preferred)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Track and calculate uptime monthly. Exclude scheduled maintenance windows by notifying Plaid in advance.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Success rate (conversion)`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`> 70%`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Percentage of OAuth flows that complete successfully from authorization to token exchange.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Data completeness`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`100% fill rate`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`All required fields populated in responses (no null values for required fields).`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Error rate`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`< 1% server errors`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`HTTP 500 errors and other server-side failures. User-driven outcomes (cancelled flows, invalid credentials) are counted separately and typically add up to another 1%.`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "why-these-numbers-matter"
      }}>{`Why these numbers matter`}</h3>
      <p><strong parentName="p">{`Latency:`}</strong>{` Slow responses increase user drop-off rates. Sub-3.5 second responses keep users engaged.`}</p>
      <p><strong parentName="p">{`Throughput:`}</strong>{` Connection volumes can spike during peak times. Insufficient throughput creates bottlenecks that cascade to downstream apps.`}</p>
      <p><strong parentName="p">{`Uptime:`}</strong>{` Downtime blocks new connections and refresh flows. In payment use cases, even 30 minutes of downtime can lead to ACH failures and customer complaints.`}</p>
      <p><strong parentName="p">{`Conversion rate:`}</strong>{` Low conversion indicates UX problems (confusing screens, slow responses) or technical issues (timeout errors, redirect failures).`}</p>
      <h2 {...{
        "id": "monitoring-recommendations"
      }}>{`Monitoring recommendations`}</h2>
      <p>{`Track these metrics continuously:`}</p>
      <ul>
        <li parentName="ul">{`P50, P95, P99 latency for each OAuth endpoint`}</li>
        <li parentName="ul">{`HTTP status code distribution (200, 400, 401, 500, 503)`}</li>
        <li parentName="ul">{`OAuth flow completion rate (authorization → token exchange success)`}</li>
        <li parentName="ul">{`Token refresh success rate`}</li>
        <li parentName="ul">{`JWKS endpoint availability and latency`}</li>
      </ul>
      <p>{`Set up alerts for:`}</p>
      <ul>
        <li parentName="ul">{`Latency P95 exceeding 3.5 seconds`}</li>
        <li parentName="ul">{`Total error rate (server plus user-driven) exceeding 2%`}</li>
        <li parentName="ul">{`Uptime dropping below 99.5%`}</li>
        <li parentName="ul">{`Throughput capacity issues`}</li>
      </ul>
      <h3 {...{
        "id": "measure-each-step-of-the-flow-not-just-the-total"
      }}>{`Measure each step of the flow, not just the total`}</h3>
      <p>{`A single completion rate tells you that users are dropping out, but not where. Count users reaching each step of the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-flow"
        }}>{`OAuth flow`}</a>{` separately, then compare consecutive steps to find where they leave:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Step`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`What to count`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Authorization request`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Requests received at your `}<inlineCode parentName="td">{`authorization_endpoint`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Login`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Credentials submitted, and how many succeed`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`MFA`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Second-factor prompts issued, and how many are completed`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Account selection`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Only if you host this screen rather than Plaid`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Authorization code`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Redirects back to Plaid carrying a `}<inlineCode parentName="td">{`code`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Token exchange`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Requests to your `}<inlineCode parentName="td">{`token_endpoint`}</inlineCode>{` that return tokens`}</td>
          </tr>
        </tbody>
      </table>
      <p>{`The largest gap between two consecutive steps is where to focus. If it falls at MFA, check your prompts against the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-flow#step-2---the-user-completes-all-authentication-steps"
        }}>{`2FA requirements`}</a>{`, since both delivery timing and message clarity affect completion. If it falls between login and the redirect, confirm your server returns and logs OAuth errors rather than failing silently.`}</p>
      <p>{`Plaid reports its own view of conversion in the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/post-launch/dashboard"
        }}>{`Dashboard`}</a>{`, which is useful for comparison, but only your logs show the steps that happen entirely on your domain.`}</p>
      <h2 {...{
        "id": "capacity-planning"
      }}>{`Capacity planning`}</h2>
      <p>{`Plan for growth:`}</p>
      <ul>
        <li parentName="ul">{`Volume projections for traffic increases over time`}</li>
        <li parentName="ul">{`Peak load during traffic spikes`}</li>
        <li parentName="ul">{`Refresh traffic from background calls (can generate significant additional load)`}</li>
        <li parentName="ul">{`JWKS caching (Plaid caches for up to 10 minutes, reducing load on that endpoint)`}</li>
      </ul>
      <p>{`Test your infrastructure under load before going live. Use load-testing tools to simulate hundreds of concurrent OAuth flows and verify that performance holds up.`}</p>
      <h2 {...{
        "id": "logging-identifiers-for-troubleshooting"
      }}>{`Logging identifiers for troubleshooting`}</h2>
      <p>{`Log the following identifiers to aid in troubleshooting issues with Plaid:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`OAuth `}<inlineCode parentName="strong">{`state`}</inlineCode>{` parameter:`}</strong>{` Plaid sends a unique string in the `}<inlineCode parentName="li">{`state`}</inlineCode>{` parameter when redirecting the user's browser to the `}<inlineCode parentName="li">{`authorization_endpoint`}</inlineCode>{`. Useful in debugging OAuth flow errors.`}</li>
        <li parentName="ul"><strong parentName="li"><inlineCode parentName="strong">{`X-Request-ID`}</inlineCode>{` header:`}</strong>{` Plaid sends this header in requests to your resource server. It can be correlated with error logs in the Dashboard's Integration Health view.`}</li>
        <li parentName="ul"><strong parentName="li">{`User identifier:`}</strong>{` Useful in debugging the request flow for a given user. Depending on how you set up your API, this value can be from the following sources:`}
          <ul parentName="li">
            <li parentName="ul">{`The value of `}<inlineCode parentName="li">{`sub`}</inlineCode>{` from your OIDC token`}</li>
            <li parentName="ul">{`The value of `}<inlineCode parentName="li">{`sub`}</inlineCode>{` from your OIDC `}<inlineCode parentName="li">{`userinfo_endpoint`}</inlineCode></li>
            <li parentName="ul">{`Value of `}<inlineCode parentName="li">{`customerId`}</inlineCode>{` from the `}<inlineCode parentName="li">{`/customers/current`}</inlineCode>{` endpoint`}</li>
          </ul>
        </li>
      </ul>
      <h2 {...{
        "id": "getting-support"
      }}>{`Getting support`}</h2>
      <p>{`Once your integration is live, Data Partner Support Engineering (DPSE) is your primary point of contact.`}</p>
      <p>{`Submit support tickets from the Dashboard by navigating to `}<strong parentName="p">{`Get Help`}</strong>{` → `}<strong parentName="p">{`Open New Case`}</strong>{` → `}<strong parentName="p">{`Open Finance Issue`}</strong>{`. Tickets are routed directly to the DPSE team for triage. You can also reach support directly at `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new"
        }}>{`https://dashboard.plaid.com/support/new`}</a>{`.`}</p>
      <p>{`When submitting a support request, include:`}</p>
      <ul>
        <li parentName="ul">{`Institution name and ID if available`}</li>
        <li parentName="ul">{`Environment configuration`}</li>
        <li parentName="ul">{`Timestamp and approximate time range`}</li>
        <li parentName="ul">{`Relevant request IDs`}</li>
        <li parentName="ul">{`Description of the issue and expected behavior`}</li>
        <li parentName="ul">{`Whether the issue occurred during testing or production traffic`}</li>
      </ul>
      <p>{`Solutions Engineering may re-engage for major expansions or architectural changes.`}</p>
      <h2 {...{
        "id": "best-practices"
      }}>{`Best practices`}</h2>
      <ul>
        <li parentName="ul">{`Notify Plaid of any planned outages or downtime at least 24 hours in advance by emailing `}<inlineCode parentName="li">{`plaid_bank_traffic@plaid.com`}</inlineCode>{`.`}</li>
        <li parentName="ul">{`Ensure that you are logging `}<a parentName="li" {...{
            "href": "#logging-identifiers-for-troubleshooting"
          }}>{`User Identifier and OAuth State`}</a>{` to help troubleshoot your integration going forward.`}</li>
        <li parentName="ul">{`When reaching out about a user issue, providing a User Identifier enables faster troubleshooting of specific user connection issues.`}</li>
      </ul>
      <h2 {...{
        "id": "what-to-explore-next"
      }}>{`What to explore next`}</h2>
      <p>{`After going live, consider ways to strengthen your integration:`}</p>
      <ul>
        <li parentName="ul">{`Increase user throughput with `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/app2app"
          }}>{`App2App functionality`}</a></li>
        <li parentName="ul">{`Get visibility into connected applications with the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/permissions-manager"
          }}>{`App Directory`}</a>{`, and into user consent with `}<a parentName="li" {...{
            "href": "/core-exchange/docs/consent-management"
          }}>{`consent management`}</a></li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 2,
        title: "Overview",
      },
{
        id: "target-metrics",
        level: 2,
        title: "Target metrics",
      },
{
        id: "why-these-numbers-matter",
        level: 3,
        title: "Why these numbers matter",
      },
{
        id: "monitoring-recommendations",
        level: 2,
        title: "Monitoring recommendations",
      },
{
        id: "measure-each-step-of-the-flow-not-just-the-total",
        level: 3,
        title: "Measure each step of the flow, not just the total",
      },
{
        id: "capacity-planning",
        level: 2,
        title: "Capacity planning",
      },
{
        id: "logging-identifiers-for-troubleshooting",
        level: 2,
        title: "Logging identifiers for troubleshooting",
      },
{
        id: "getting-support",
        level: 2,
        title: "Getting support",
      },
{
        id: "best-practices",
        level: 2,
        title: "Best practices",
      },
{
        id: "what-to-explore-next",
        level: 2,
        title: "What to explore next",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
